import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {ProfileMain} from 'src/components/profile-tab-structure';
import {SaveWithReasonButton} from 'src/components/save-with-reason/save-with-reason-button';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useNewsletterDictionaryForUser} from 'src/hooks/dictionaries/useNewsletterDictionaryForUser';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {BrowserLimitsSection, useUserBrowserLimits} from 'src/modules/profile/account/create-user/steps/users-settings/components/browser-limits-configuration';
import {NewslettersSettings, useUserNewsletter} from 'src/modules/profile/account/create-user/steps/users-settings/components/newsletters-settings';
import {NewsletterHistoryView} from 'src/modules/profile/account/create-user/steps/users-settings/components/newsletters-settings/newsletter-history.view';
import {useNewsletterHistory} from 'src/modules/profile/account/create-user/steps/users-settings/components/newsletters-settings/useNewsletterHistory';
import {
    PbEditorialSettingsSection, useAnalystSettings, useMarketMapsSettings,
} from 'src/modules/profile/account/create-user/steps/users-settings/components/pb-editorial-settings';
import {
    PbWorkspaceSettingsSection, useWorkspaceSettings,
} from 'src/modules/profile/account/create-user/steps/users-settings/components/workspace-settings';


import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {ImportContent} from './import-content.component';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';


type Props = {
    userId: number,
    accountId: number,
};

export const UserSettings = ({userId, accountId}: Props) => {
    const [busy, setBusy] = useState<boolean>(false);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {canEditUser} = useAdminAccessRight();
    const {userFieldChange, userSubmit} = useEventTracking();

    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'user-settings',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const onFieldChange = (section: string) => (parameterName: string) => {
        userFieldChange({
            section,
            viewType: 'Existed',
            parameterName,
        });
    };

    const {
        isLoading: isUserNewsletterLoading,
        loadingError: newsletterLoadingError,
        isChanged: isChangedUserNewsletter,

        data: newsletterTypeCodes,
        setData: setNewsletterTypeCodes,

        saveChanges: saveUserNewsletter,
        discardChanges: discardUserNewsletter,
    } = useUserNewsletter({userId});

    const {fullNewsletterDictionary, availableNewsletterTypes} = useNewsletterDictionaryForUser({userId});

    const {
        isLoading: isNewsletterHistoryLoading,
        loadingError: newsletterHistoryLoadingError,

        data: newsletterHistory,
        filters,
        sortDirection,
        setSortDirection,

        applyFilters,
        clearFilters,
    } = useNewsletterHistory({userId, availableNewsletterTypes});

    const {
        isLoading: isBrowserLimitsLoading,
        loadingError: browserLimitsError,
        data: browserLimits,
        setData: setBrowserLimits,
        isChanged: isBrowserLimitsChanged,
        saveChanges: onSaveBrowserRegistrations,
        discardChanges: discardBrowserLimitsChanges,
    } = useUserBrowserLimits({userId});

    const {
        isLoading: isWorkspaceSettingsLoading,
        loadingError: workspaceSettingsLoadingError,
        isChanged: isWorkspaceSettingsChanged,
        data: workspaceSettings,
        setData: setWorkspaceSettings,
        saveChanges: saveWorkspaceSettings,
        discardChanges: discardWorkspaceSettings,
    } = useWorkspaceSettings({userId});

    const {
        isLoading: isAnalystSettingsLoading,
        loadingError: analystSettingsLoadingError,
        isChanged: isAnalystSettingsChanged,
        data: analystSettings,
        setData: setAnalystSettings,
        saveChanges: saveAnalystSettings,
        discardChanges: discardAnalystSettings,
    } = useAnalystSettings({userId});

    const {
        isLoading: isMarketMapsSettingsLoading,
        loadingError: marketMapsSettingsLoadingError,
        isChanged: isMarketMapsSettingsChanged,
        data: marketMapsSettings,
        setData: setMarketMapsSettings,
        saveChanges: saveMarketMapsSettings,
        discardChanges: discardMarketMapsSettings,
    } = useMarketMapsSettings({userId});

    useEffect(() => {
        setIsChunkChanged(
            isChangedUserNewsletter ||
            isBrowserLimitsChanged ||
            isAnalystSettingsChanged ||
            isWorkspaceSettingsChanged ||
            isMarketMapsSettingsChanged,
        );
    }, [
        setIsChunkChanged,
        isChangedUserNewsletter, isBrowserLimitsChanged, isAnalystSettingsChanged,
        isMarketMapsSettingsChanged, isWorkspaceSettingsChanged,
    ]);

    const discardChanges = useCallback(() => {
        discardUserNewsletter();
        discardBrowserLimitsChanges();
        discardAnalystSettings();
        discardWorkspaceSettings();
        discardMarketMapsSettings();
        userSubmit({place: 'User Settings', submit: false});

        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [
        setSuccessMessage, userSubmit,
        discardUserNewsletter, discardAnalystSettings,
        discardBrowserLimitsChanges, discardMarketMapsSettings,
        discardWorkspaceSettings,
    ]);

    const saveChanges = useCallback(async (options?: SaveOptions) => {
        userSubmit({place: 'User Settings', submit: true});
        setBusy(true);
        try {
            const calls = [];

            if (isChangedUserNewsletter) {
                calls.push(saveUserNewsletter(options));
            }

            if (isBrowserLimitsChanged) {
                calls.push(onSaveBrowserRegistrations(options));
            }

            if (isAnalystSettingsChanged) {
                calls.push(saveAnalystSettings(options));
            }

            if (isWorkspaceSettingsChanged) {
                calls.push(saveWorkspaceSettings(options));
            }

            if (isMarketMapsSettingsChanged) {
                calls.push(saveMarketMapsSettings(options));
            }

            const errors = [...new Set((await Promise.all(calls)).map(error => error).filter(Boolean))];
            setBusy(false);

            if (errors.length) {
                setErrorMessage(errors.join(' '));
                return errors.join(' ');
            } else {
                proceed();
            }

        } finally {
            setBusy(false);
        }
    }, [
        isBrowserLimitsChanged,
        onSaveBrowserRegistrations,
        isChangedUserNewsletter,
        saveUserNewsletter,
        isAnalystSettingsChanged,
        saveAnalystSettings,
        isMarketMapsSettingsChanged,
        saveMarketMapsSettings,
        isWorkspaceSettingsChanged,
        proceed,
        setErrorMessage,
        userSubmit,
        saveWorkspaceSettings,
    ]);

    const editDisabled = useMemo(() => !canEditUser(accountId), [accountId, canEditUser]);

    return (
        <ValidationContextProvider>
            <ProfileMain header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            User Settings
                        </Typography>
                        {!editDisabled && (
                            <Group>
                                {!!accountId && (
                                    <ImportContent userId={userId} accountId={accountId}/>
                                )}
                                <Button
                                    variant="Type.SECONDARY"
                                    disabled={!isCurrentChunkChanged}
                                    onClick={discardChanges}>
                                    Discard Changes
                                </Button>
                                <SaveWithReasonButton
                                    busy={busy}
                                    disabled={!isCurrentChunkChanged}
                                    onSave={saveChanges}
                                />
                            </Group>
                        )}
                    </Group>
                </Item>
            )}>
                <NewslettersSettings {...{
                    isLoading: isUserNewsletterLoading,
                    loadingError: newsletterLoadingError,
                    newsletterTypeCodes,
                    setNewsletterTypeCodes,
                    onFieldChange: onFieldChange('Newsletter Settings'),
                    disabled: editDisabled,
                    newsletterDictionary: fullNewsletterDictionary,
                    availableNewsletterTypes,
                    newsletterHistory: (
                        <NewsletterHistoryView {...{
                            isNewsletterHistoryLoading,
                            newsletterHistoryLoadingError,

                            newsletterHistory,
                            filters,
                            sortDirection,
                            setSortDirection,

                            applyFilters,
                            clearFilters,
                            newsletterDictionary: fullNewsletterDictionary,
                            availableNewsletterTypes,
                        }}
                        />
                    ),
                }}
                />
                <BrowserLimitsSection
                    disabled={editDisabled}
                    isLoading={isBrowserLimitsLoading}
                    loadingError={browserLimitsError}
                    browserLimits={browserLimits}
                    setBrowserLimits={setBrowserLimits}
                    onFieldChange={onFieldChange('Browser Limits')}
                />
                <PbEditorialSettingsSection
                    disabled={editDisabled}
                    isLoading={isAnalystSettingsLoading || isMarketMapsSettingsLoading}
                    loadingError={analystSettingsLoadingError || marketMapsSettingsLoadingError}
                    analystSettings={analystSettings}
                    marketMapsSettings={marketMapsSettings}
                    setAnalystSettings={setAnalystSettings}
                    setMarketMapsSettings={setMarketMapsSettings}
                    onFieldChange={onFieldChange('Editorial settings')}
                />
                <PbWorkspaceSettingsSection
                    disabled={editDisabled}
                    isLoading={isWorkspaceSettingsLoading}
                    loadingError={workspaceSettingsLoadingError}
                    workspaceSettings={workspaceSettings}
                    setWorkspaceSettings={setWorkspaceSettings}
                    onFieldChange={onFieldChange('Workspace settings')}
                />
            </ProfileMain>
        </ValidationContextProvider>
    );
};
